import httpRequest from './httpRequest';
import { PROFILE, createProtectedBlobFromFile, inferContentType } from './assetCrypto';

const DEFAULT_IMAGE_THUMB_KB = 120;
const DEFAULT_IMAGE_THUMB_MIME = 'image/jpeg';
const AUTO_INLINE_IMAGE_LIMIT_BYTES = 512 * 1024;
const AUTO_IMAGE_THUMB_MAX_EDGE = 1280;
const AUTO_IMAGE_THUMB_SAVING_RATIO = 0.9;
const HIGH_FIDELITY_IMAGE_TYPES = new Set(['image/png', 'image/webp']);
const VIDEO_METADATA_TIMEOUT_MS = 5000;
const VIDEO_FRAME_TIMEOUT_MS = 1200;

function parseAction(action) {
	const normalized = String(action || '').trim();
	const queryIndex = normalized.indexOf('?');
	const basePath = queryIndex < 0 ? normalized : normalized.slice(0, queryIndex);
	const queryText = queryIndex < 0 ? '' : normalized.slice(queryIndex + 1);
	return {
		basePath,
		query: new URLSearchParams(queryText)
	};
}

function normalizeUploadType(path) {
	if (path.includes('/image/upload')) {
		return 'image';
	}
	if (path.includes('/video/upload')) {
		return 'video';
	}
	if (path.includes('/audio/upload')) {
		return 'audio';
	}
	if (path.includes('/file/upload')) {
		return 'file';
	}
	throw new Error('不支持的上传类型');
}

function buildInitUrl(basePath) {
	return `${basePath}/init`;
}

function buildCompleteUrl(basePath) {
	return `${basePath}/complete`;
}

function normalizeAssetId(assetId) {
	return assetId == null ? '' : String(assetId);
}

function getFileExtension(fileName) {
	const normalized = String(fileName || '').trim();
	const index = normalized.lastIndexOf('.');
	return index < 0 ? '' : normalized.slice(index + 1).toLowerCase();
}

function normalizeContentType(file) {
	return inferContentType(file && file.type, file && file.name, '');
}

function normalizeUploadUrl(uploadUrl) {
	return String(uploadUrl || '').trim();
}

function toObjectUrl(file) {
	return URL.createObjectURL(file);
}

function revokeObjectUrl(url) {
	if (url && url.startsWith('blob:')) {
		URL.revokeObjectURL(url);
	}
}

function waitImageLoaded(img) {
	return new Promise((resolve, reject) => {
		img.onload = () => resolve();
		img.onerror = () => reject(new Error('图片读取失败'));
	});
}

function waitVideoLoaded(video, timeoutMs = VIDEO_METADATA_TIMEOUT_MS) {
	return new Promise((resolve, reject) => {
		let settled = false;
		let timer = null;
		const cleanup = () => {
			if (timer) {
				clearTimeout(timer);
			}
			video.onloadedmetadata = null;
			video.onerror = null;
		};
		const finish = (handler, value) => {
			if (settled) {
				return;
			}
			settled = true;
			cleanup();
			handler(value);
		};
		video.onloadedmetadata = () => finish(resolve);
		video.onerror = () => finish(reject, new Error('视频读取失败'));
		timer = setTimeout(() => finish(reject, new Error('视频读取超时')), timeoutMs);
	});
}

function waitVideoFrame(video, currentTime, timeoutMs = VIDEO_FRAME_TIMEOUT_MS) {
	return new Promise((resolve, reject) => {
		let settled = false;
		let timer = null;
		const cleanup = () => {
			if (timer) {
				clearTimeout(timer);
			}
			video.onseeked = null;
			video.onerror = null;
		};
		const finish = (handler, value) => {
			if (settled) {
				return;
			}
			settled = true;
			cleanup();
			handler(value);
		};
		const done = () => finish(resolve);
		video.onseeked = done;
		video.onerror = () => finish(reject, new Error('视频封面提取失败'));
		try {
			video.currentTime = Math.max(0, currentTime || 0);
		} catch (error) {
			done();
		}
		timer = setTimeout(done, timeoutMs);
	});
}

function canvasToBlob(canvas, type, quality) {
	return new Promise((resolve, reject) => {
		canvas.toBlob((blob) => {
			if (blob) {
				resolve(blob);
				return;
			}
			reject(new Error('资源处理失败'));
		}, type, quality);
	});
}

async function readImageDimensions(file) {
	const objectUrl = toObjectUrl(file);
	try {
		const img = new Image();
		img.decoding = 'async';
		img.src = objectUrl;
		await waitImageLoaded(img);
		return {
			width: img.naturalWidth || img.width || 0,
			height: img.naturalHeight || img.height || 0
		};
	} finally {
		revokeObjectUrl(objectUrl);
	}
}

async function buildImageThumbnail(file, targetKb) {
	const normalizedTargetKb = Number(targetKb || 0);
	if (!file || !Number.isFinite(normalizedTargetKb) || normalizedTargetKb <= 0) {
		return null;
	}
	const maxBytes = normalizedTargetKb * 1024;
	if (file.size <= maxBytes) {
		return null;
	}
	const objectUrl = toObjectUrl(file);
	try {
		const img = new Image();
		img.decoding = 'async';
		img.src = objectUrl;
		await waitImageLoaded(img);
		const canvas = document.createElement('canvas');
		const context = canvas.getContext('2d');
		if (!context) {
			return null;
		}
		const maxEdge = AUTO_IMAGE_THUMB_MAX_EDGE;
		const ratio = Math.min(1, maxEdge / Math.max(img.naturalWidth || 1, img.naturalHeight || 1));
		canvas.width = Math.max(1, Math.round((img.naturalWidth || img.width || 1) * ratio));
		canvas.height = Math.max(1, Math.round((img.naturalHeight || img.height || 1) * ratio));
		context.drawImage(img, 0, 0, canvas.width, canvas.height);
		const preferredMimeType = resolveImageThumbMimeType(file);
		const candidateQueue = buildImageThumbCandidateQueue(preferredMimeType);
		let bestBlob = null;
		for (const candidate of candidateQueue) {
			let blob = null;
			try {
				blob = await canvasToBlob(canvas, candidate.type, candidate.quality);
			} catch (error) {
				continue;
			}
			if (!bestBlob || blob.size < bestBlob.size) {
				bestBlob = blob;
			}
			if (blob.size <= maxBytes) {
				bestBlob = blob;
				break;
			}
		}
		if (!bestBlob || bestBlob.size >= file.size * AUTO_IMAGE_THUMB_SAVING_RATIO) {
			return null;
		}
		const thumbMimeType = bestBlob.type || preferredMimeType;
		const thumbName = buildImageThumbFileName(file, thumbMimeType);
		return new File([bestBlob], thumbName, {
			type: thumbMimeType
		});
	} finally {
		revokeObjectUrl(objectUrl);
	}
}

function resolveImageThumbMimeType(file) {
	const fileType = normalizeContentType(file);
	if (HIGH_FIDELITY_IMAGE_TYPES.has(fileType)) {
		return fileType;
	}
	return DEFAULT_IMAGE_THUMB_MIME;
}

function buildImageThumbCandidateQueue(preferredMimeType) {
	const queue = [];
	const addCandidate = (type, quality = undefined) => {
		if (!type) {
			return;
		}
		if (queue.some((item) => item.type === type && item.quality === quality)) {
			return;
		}
		queue.push({ type, quality });
	};
	if (preferredMimeType === 'image/png') {
		addCandidate('image/png');
		addCandidate('image/webp', 0.92);
		addCandidate('image/webp', 0.84);
	} else if (preferredMimeType === 'image/webp') {
		addCandidate('image/webp', 0.92);
		addCandidate('image/webp', 0.84);
		addCandidate('image/webp', 0.76);
	} else {
		addCandidate(DEFAULT_IMAGE_THUMB_MIME, 0.9);
		addCandidate(DEFAULT_IMAGE_THUMB_MIME, 0.82);
		addCandidate(DEFAULT_IMAGE_THUMB_MIME, 0.72);
	}
	addCandidate(DEFAULT_IMAGE_THUMB_MIME, 0.86);
	addCandidate(DEFAULT_IMAGE_THUMB_MIME, 0.76);
	return queue;
}

function buildImageThumbFileName(file, mimeType) {
	const baseName = String(file && file.name ? file.name : 'thumb').replace(/\.[^.]+$/, '') || 'thumb';
	const extByMime = {
		'image/png': 'png',
		'image/webp': 'webp',
		'image/jpeg': 'jpg'
	};
	return `${baseName}_thumb.${extByMime[mimeType] || 'jpg'}`;
}

function resolveImageThumbSizeKb(file, actionInfo) {
	const rawThumbSize = actionInfo && actionInfo.query ? actionInfo.query.get('thumbSize') : null;
	if (rawThumbSize == null || String(rawThumbSize).trim() === '') {
		const sourceSize = Number(file && file.size) || 0;
		if (sourceSize <= AUTO_INLINE_IMAGE_LIMIT_BYTES) {
			return 0;
		}
		if (sourceSize <= 2 * 1024 * 1024) {
			return 256;
		}
		if (sourceSize <= 6 * 1024 * 1024) {
			return 384;
		}
		return 512;
	}
	const parsedThumbSize = Number(rawThumbSize);
	if (!Number.isFinite(parsedThumbSize)) {
		return DEFAULT_IMAGE_THUMB_KB;
	}
	return parsedThumbSize > 0 ? parsedThumbSize : 0;
}

async function readVideoMetadata(file) {
	const objectUrl = toObjectUrl(file);
	try {
		const video = document.createElement('video');
		video.preload = 'metadata';
		video.muted = true;
		video.src = objectUrl;
		await waitVideoLoaded(video);
		return {
			width: video.videoWidth || 0,
			height: video.videoHeight || 0,
			duration: Number(video.duration) || 0
		};
	} finally {
		revokeObjectUrl(objectUrl);
	}
}

async function safeReadVideoMetadata(file) {
	try {
		return await readVideoMetadata(file);
	} catch (error) {
		return {
			width: 0,
			height: 0,
			duration: 0
		};
	}
}

async function buildVideoCover(file) {
	const objectUrl = toObjectUrl(file);
	try {
		const video = document.createElement('video');
		video.preload = 'metadata';
		video.muted = true;
		video.playsInline = true;
		video.src = objectUrl;
		await waitVideoLoaded(video);
		await waitVideoFrame(video, Math.min((Number(video.duration) || 0) / 3, 1));
		const width = video.videoWidth || 0;
		const height = video.videoHeight || 0;
		if (!width || !height) {
			return null;
		}
		const canvas = document.createElement('canvas');
		const maxEdge = 720;
		const ratio = Math.min(1, maxEdge / Math.max(width, height));
		canvas.width = Math.max(1, Math.round(width * ratio));
		canvas.height = Math.max(1, Math.round(height * ratio));
		const context = canvas.getContext('2d');
		if (!context) {
			return null;
		}
		context.drawImage(video, 0, 0, canvas.width, canvas.height);
		const blob = await canvasToBlob(canvas, 'image/jpeg', 0.8);
		const coverName = `${file.name.replace(/\.[^.]+$/, '') || 'video'}_cover.jpg`;
		return new File([blob], coverName, {
			type: 'image/jpeg'
		});
	} catch (error) {
		return null;
	} finally {
		revokeObjectUrl(objectUrl);
	}
}

function resolveVideoCoverSize(meta = {}) {
	const width = Number(meta.width) || 0;
	const height = Number(meta.height) || 0;
	if (!width || !height) {
		return {
			width: 720,
			height: 405
		};
	}
	const maxEdge = 720;
	const ratio = Math.min(1, maxEdge / Math.max(width, height));
	return {
		width: Math.max(240, Math.round(width * ratio)),
		height: Math.max(135, Math.round(height * ratio))
	};
}

async function buildVideoFallbackCover(file, meta = {}) {
	const canvas = document.createElement('canvas');
	const context = canvas.getContext('2d');
	if (!context) {
		return null;
	}
	const size = resolveVideoCoverSize(meta);
	canvas.width = size.width;
	canvas.height = size.height;
	const gradient = context.createLinearGradient(0, 0, canvas.width, canvas.height);
	gradient.addColorStop(0, '#1f2937');
	gradient.addColorStop(1, '#4b5563');
	context.fillStyle = gradient;
	context.fillRect(0, 0, canvas.width, canvas.height);

	context.fillStyle = 'rgba(255, 255, 255, 0.12)';
	context.beginPath();
	context.arc(canvas.width / 2, canvas.height / 2, Math.min(canvas.width, canvas.height) * 0.18, 0, Math.PI * 2);
	context.fill();

	const triangleSize = Math.min(canvas.width, canvas.height) * 0.1;
	context.fillStyle = 'rgba(255, 255, 255, 0.92)';
	context.beginPath();
	context.moveTo(canvas.width / 2 - triangleSize * 0.45, canvas.height / 2 - triangleSize);
	context.lineTo(canvas.width / 2 - triangleSize * 0.45, canvas.height / 2 + triangleSize);
	context.lineTo(canvas.width / 2 + triangleSize * 1.1, canvas.height / 2);
	context.closePath();
	context.fill();

	const blob = await canvasToBlob(canvas, 'image/jpeg', 0.82);
	const coverName = `${file.name.replace(/\.[^.]+$/, '') || 'video'}_cover.jpg`;
	return new File([blob], coverName, {
		type: 'image/jpeg'
	});
}

function protectFileObject(file, profile) {
	return createProtectedBlobFromFile(file, {
		profile,
		mimeType: normalizeContentType(file),
		fileName: file.name
	});
}

async function putProtectedObject(uploadUrl, protectedPayload, contentType, uploadMethod = 'PUT', uploadHeaders = {}) {
	const normalizedContentType = String(contentType || '').trim() || 'application/octet-stream';
	const response = await fetch(normalizeUploadUrl(uploadUrl), {
		method: String(uploadMethod || 'PUT').toUpperCase(),
		mode: 'cors',
		credentials: 'omit',
		headers: {
			'Content-Type': normalizedContentType,
			...uploadHeaders
		},
		body: protectedPayload
	});
	if (!response.ok) {
		throw new Error(`对象上传失败(${response.status})`);
	}
}

async function uploadImage(file, actionInfo, options) {
	const thumbSizeKb = resolveImageThumbSizeKb(file, actionInfo);
	const [dimensions, thumbFile, originProtected] = await Promise.all([
		readImageDimensions(file),
		buildImageThumbnail(file, thumbSizeKb),
		protectFileObject(file, PROFILE.STATIC_SHIFT_4K)
	]);
	const thumbProtected = thumbFile ? await protectFileObject(thumbFile, PROFILE.STATIC_SHIFT_4K) : null;
	const initResult = await httpRequest({
		url: buildInitUrl(actionInfo.basePath),
		method: 'post',
		data: {
			fileName: file.name,
			contentType: normalizeContentType(file),
			size: file.size,
			isPermanent: Boolean(options.isPermanent),
			thumbFileName: thumbFile ? thumbFile.name : '',
			thumbContentType: thumbFile ? normalizeContentType(thumbFile) : '',
			thumbSize: thumbFile ? thumbFile.size : 0
		},
		timeout: options.timeout
	});
	const uploadTasks = [
		putProtectedObject(initResult.originUploadUrl, originProtected.payloadBlob, normalizeContentType(file))
	];
	if (thumbFile && thumbProtected && initResult.thumbUploadUrl) {
		uploadTasks.push(
			putProtectedObject(initResult.thumbUploadUrl, thumbProtected.payloadBlob, normalizeContentType(thumbFile))
		);
	}
	await Promise.all(uploadTasks);
	return httpRequest({
		url: buildCompleteUrl(actionInfo.basePath),
		method: 'post',
		data: {
			assetId: normalizeAssetId(initResult.assetId),
			storagePath: initResult.originStoragePath,
			thumbStoragePath: thumbFile ? initResult.thumbStoragePath : '',
			width: dimensions.width,
			height: dimensions.height
		},
		timeout: options.timeout
	});
}

async function uploadVideo(file, actionInfo, options) {
	const [meta, rawCoverFile, videoProtected] = await Promise.all([
		safeReadVideoMetadata(file),
		buildVideoCover(file),
		protectFileObject(file, PROFILE.MEDIA_SHIFT_64K)
	]);
	const coverFile = rawCoverFile || await buildVideoFallbackCover(file, meta);
	const coverProtected = coverFile ? await protectFileObject(coverFile, PROFILE.STATIC_SHIFT_4K) : null;
	const container = getFileExtension(file.name) || 'mp4';
	const initResult = await httpRequest({
		url: buildInitUrl(actionInfo.basePath),
		method: 'post',
		data: {
			fileName: file.name,
			contentType: normalizeContentType(file),
			size: file.size,
			coverFileName: coverFile ? coverFile.name : '',
			coverContentType: coverFile ? normalizeContentType(coverFile) : '',
			coverSize: coverFile ? coverFile.size : 0,
			originalContainer: container,
			uploadContainer: container,
			uploadMode: 'DIRECT_UPLOAD'
		},
		timeout: options.timeout
	});
	await putProtectedObject(initResult.videoUploadUrl, videoProtected.payloadBlob, normalizeContentType(file));
	if (coverFile && coverProtected && initResult.coverUploadUrl) {
		await putProtectedObject(initResult.coverUploadUrl, coverProtected.payloadBlob, normalizeContentType(coverFile));
	}
	return httpRequest({
		url: buildCompleteUrl(actionInfo.basePath),
		method: 'post',
		data: {
			assetId: normalizeAssetId(initResult.assetId),
			storagePath: initResult.videoStoragePath,
			coverStoragePath: coverFile ? initResult.coverStoragePath : '',
			width: meta.width,
			height: meta.height
		},
		timeout: options.timeout
	});
}

async function uploadFileLike(file, actionInfo, options, profile, initDataFactory) {
	const protectedPayload = await protectFileObject(file, profile);
	const initResult = await httpRequest({
		url: buildInitUrl(actionInfo.basePath),
		method: 'post',
		data: initDataFactory(file),
		timeout: options.timeout
	});
	await putProtectedObject(initResult.uploadUrl, protectedPayload.payloadBlob, normalizeContentType(file));
	return httpRequest({
		url: buildCompleteUrl(actionInfo.basePath),
		method: 'post',
		data: {
			assetId: normalizeAssetId(initResult.assetId),
			storagePath: initResult.storagePath
		},
		timeout: options.timeout
	});
}

async function uploadAudio(file, actionInfo, options) {
	return uploadFileLike(file, actionInfo, options, PROFILE.MEDIA_SHIFT_64K, (currentFile) => {
		const container = getFileExtension(currentFile.name) || 'bin';
		return {
			fileName: currentFile.name,
			contentType: normalizeContentType(currentFile),
			size: currentFile.size,
			originalContainer: container,
			uploadContainer: container,
			uploadMode: 'DIRECT_UPLOAD'
		};
	});
}

async function uploadFileAsset(file, actionInfo, options) {
	return uploadFileLike(file, actionInfo, options, PROFILE.STATIC_SHIFT_4K, (currentFile) => ({
		fileName: currentFile.name,
		contentType: normalizeContentType(currentFile),
		size: currentFile.size
	}));
}

async function uploadProtectedAsset(file, options = {}) {
	if (!file) {
		throw new Error('文件不能为空');
	}
	const actionInfo = parseAction(options.action);
	if (!actionInfo.basePath) {
		throw new Error('上传地址为空');
	}
	switch (normalizeUploadType(actionInfo.basePath)) {
		case 'image':
			return uploadImage(file, actionInfo, options);
		case 'video':
			return uploadVideo(file, actionInfo, options);
		case 'audio':
			return uploadAudio(file, actionInfo, options);
		case 'file':
			return uploadFileAsset(file, actionInfo, options);
		default:
			throw new Error('不支持的上传类型');
	}
}

export {
	uploadProtectedAsset
};
